import type { FaqItem } from '@/lib/seo';
import { buildEditorialLinkMetadata, type EditorialLinkMetadata } from '@/lib/internal-linking';

export type SeoPageKey =

  | 'sovereign-ai-canada'
  | 'ai-model-training'
  | 'small-language-model-training'
  | 'private-ai'
  | 'pipeda-compliant-ai'
  | 'healthcare-ai-canada'
  | 'legal-ai-canada'
  | 'canadian-ai-infrastructure'
  | 'rag-vs-fine-tuning'
  | 'data-residency'
  | 'security'
  | 'compliance'
  | 'canadian-ai-platform'
  | 'enterprise-ai';

export type SeoPageConfig = {
  key: SeoPageKey;
  path: string;
  title: string;
  description: string;
  keywords: string;
  badge: string;
  h1: string;
  intro: string[];
  diagramSteps: string[];
  sections: Array<{
    title: string;
    paragraphs: string[];
    bullets?: string[];
  }>;
  faqs: FaqItem[];
  relatedLinks: string[];
  ctaTitle: string;
  ctaBody: string;
  ctaHref: string;
  ctaLabel: string;
  schemaKinds?: Array<'software' | 'service'>;
  editorialLinking: EditorialLinkMetadata;
};

type BaseSeoPageConfig = Omit<SeoPageConfig, 'editorialLinking'>;

export const pageLabels: Record<string, string> = {
  '/': 'Homepage',
  '/about': 'About CanXP AI',
  '/ai-model-training': 'AI Model Training',
  '/small-language-model-training': 'Small Language Model Training',
  '/sovereign-ai-canada': 'Sovereign AI Canada',
  '/private-ai': 'Private AI',
  '/pipeda-compliant-ai': 'PIPEDA-Compliant AI',
  '/healthcare-ai-canada': 'Healthcare AI Canada',
  '/legal-ai-canada': 'Legal AI Canada',
  '/canadian-ai-infrastructure': 'Canadian AI Infrastructure',
  '/rag-vs-fine-tuning': 'RAG vs Fine-Tuning',
  '/data-residency': 'Canadian Data Residency',
  '/security': 'AI Security',
  '/compliance': 'AI Compliance',
  '/canadian-ai-platform': 'Canadian AI Platform',
  '/enterprise-ai': 'Enterprise AI',
  '/maplept': 'MaplePT',
  '/mapleos': 'MapleOS',
  '/maplenode': 'MapleNode',
  '/privacy': 'Privacy & Jurisdiction',

  '/purchase': 'Pricing & Purchase',
  '/enterprise': 'Enterprise Contact',
  '/news': 'News',
  '/insights/what-is-an-ai-operating-system': 'What Is an AI Operating System?',
  '/insights/future-of-ai-is-operational-not-conversational': 'Why the Future of AI Is Operational, Not Conversational',
};

const baseSeoPageConfigs: Record<SeoPageKey, BaseSeoPageConfig> = {

  'sovereign-ai-canada': {
    key: 'sovereign-ai-canada',
    path: '/sovereign-ai-canada',
    title: 'Sovereign AI Canada — Private AI Systems Under Canadian Jurisdiction',
    description:
      'CanXP AI helps Canadian organizations deploy sovereign AI systems, private assistants, secure knowledge bases, and model training programs under Canadian jurisdiction.',
    keywords:
      'sovereign AI Canada, Canadian sovereign AI, Canadian AI platform, private AI Canada, Canadian jurisdiction AI',
    badge: 'Sovereign AI Canada',
    h1: 'Sovereign AI for Canadian Organizations',
    intro: [
      'Canadian organizations need AI systems they can trust with sensitive knowledge, regulated data, and mission-critical workflows. CanXP AI provides a sovereign Canadian AI platform for private assistants, secure knowledge bases, model training, and enterprise deployment under Canadian jurisdiction.',
      'For healthcare, legal, public-sector, industrial, and research teams, sovereignty is not a branding layer. It is an operating requirement that shapes where data is stored, who can access it, which laws apply, and whether an AI system can be audited, governed, and deployed responsibly through [private AI infrastructure](/private-ai) and [Canadian AI infrastructure](/canadian-ai-infrastructure).',
    ],

    diagramSteps: ['Canadian jurisdiction', 'Private knowledge control', 'Model training and adaptation', 'Deployment aligned to sector risk'],
    sections: [
      {
        title: 'Why sovereign AI matters',
        paragraphs: [
          'Sovereign AI is about control. It means understanding where data is stored, who operates the infrastructure, which jurisdiction governs the service, and how models are trained and deployed.',
          'Organizations adopting AI in Canada increasingly need a clear answer to those questions. If sensitive workflows depend entirely on foreign platforms, the organization inherits foreign legal exposure, changing service terms, and limited visibility into how data and system behavior are governed.',
        ],
        bullets: [
          'Control over data residency and retention',
          'Operational visibility into model and infrastructure choices',
          'Clearer alignment with privacy and governance obligations',
          'Deployment options for regulated and confidentiality-sensitive work',
        ],
      },
      {
        title: 'What CanXP AI provides',
        paragraphs: [
          'CanXP AI combines platform, infrastructure, and model capabilities into one sovereign AI stack. Teams can deploy [MapleOS](/mapleos) as the operating environment, launch [private AI workspaces](/private-ai), operate secure knowledge bases, access model orchestration, and adapt models to specialized workflows.',
          'That makes sovereignty practical. Instead of piecing together multiple disconnected tools, organizations can approach AI as a controlled system spanning knowledge, models, deployment, and governance, then move into [AI model training](/ai-model-training) when generic models are no longer enough.',
        ],

        bullets: [
          'Private AI workspaces for professionals and teams',
          'Secure knowledge bases for confidential documents',
          'Small language model training and fine-tuning programs',
          'Canadian-hosted deployment options and architecture support',
        ],
      },
      {
        title: 'Who needs sovereign AI',
        paragraphs: [
          'The more sensitive the knowledge, the more important sovereign AI becomes. Healthcare providers, legal organizations, public institutions, industrial firms, and enterprise teams all face different forms of confidentiality, data residency, and internal governance pressure.',
          'Sovereign AI gives those organizations a path to adopt advanced AI without assuming that every workflow should flow through a foreign public API.',
        ],
        bullets: [
          'Healthcare and regional care delivery organizations',
          'Law firms and legal operations teams',
          'Public-sector and policy organizations',
          'Industrial, defence-adjacent, and scientific environments',
        ],
      },
      {
        title: 'How CanXP positions sovereignty',
        paragraphs: [
          'CanXP AI separates the major pieces of the stack clearly: CanXP AI is the sovereign platform, [MapleOS](/mapleos) is the operating environment, MaplePT is the sovereign model family, and model training is a service layer for organizations that need specialized AI behavior.',
          'That separation helps customers evaluate the right deployment path while also giving search engines and buyers a cleaner understanding of the CanXP product architecture. For the underlying logic behind that stack, see [What Is Sovereign AI and Why Does Canada Need It?](/insights/what-is-sovereign-ai-canada).',
        ],

      },
    ],
    faqs: [
      {
        question: 'What is sovereign AI?',
        answer:
          'Sovereign AI describes AI systems operated with clear control over jurisdiction, data residency, infrastructure, governance, and deployment. It focuses on who controls the system and which legal environment applies to it.',
      },
      {
        question: 'Why does Canadian jurisdiction matter for AI?',
        answer:
          'Canadian jurisdiction matters because organizations need clarity on how sensitive data is stored, accessed, retained, and governed. That is especially important for regulated, confidential, and public-interest workflows.',
      },
      {
        question: 'Is CanXP AI hosted in Canada?',
        answer:
          'CanXP AI positions its sovereign offerings around Canadian-hosted infrastructure and Canadian jurisdiction for organizations that need stronger control over where AI systems operate.',
      },
      {
        question: 'How is sovereign AI different from a foreign AI API?',
        answer:
          'A foreign AI API may still be useful for some tasks, but sovereign AI emphasizes operational control, residency, governance, and deployment options that reduce dependence on foreign-controlled services.',
      },
      {
        question: 'Who needs sovereign AI first?',
        answer:
          'Organizations with regulated data, confidential documents, specialized internal methods, or public accountability requirements usually benefit first from sovereign AI.',
      },
    ],
    relatedLinks: [
      '/canadian-ai-infrastructure',
      '/data-residency',
      '/pipeda-compliant-ai',
      '/private-ai',
      '/maplept',
      '/enterprise-ai',
    ],
    ctaTitle: 'Talk to CanXP AI about sovereign AI deployment',
    ctaBody:
      'If your organization needs AI that respects Canadian jurisdiction, sensitive workflows, and internal governance requirements, CanXP AI can help design the right deployment path.',
    ctaHref: '/enterprise',
    ctaLabel: 'Book a sovereign AI consultation',
  },
  'ai-model-training': {
    key: 'ai-model-training',
    path: '/ai-model-training',
    title: 'AI Model Training Services — Fine-Tuning, Pre-Training & Private SLMs',
    description:
      'CanXP AI helps organizations fine-tune, pre-train, quantize, and deploy private small language models on proprietary knowledge under Canadian control.',
    keywords:
      'AI model training, enterprise AI training, fine-tuning, pre-training, private model training, Canadian AI training',
    badge: 'AI Model Training Services',
    h1: 'Train AI Models on Your Organization’s Knowledge',
    intro: [
      'Most organizations are trying to force general-purpose AI systems to understand specialized knowledge through prompts, retrieval, and guardrails. That works for some workflows, but it does not always solve the deeper problem: the model itself does not understand the organization’s methods, terminology, procedures, or domain-specific reasoning.',
      'CanXP AI helps organizations train and adapt small language models for specialized work. We support fine-tuning, pre-training programs, quantization, private inference, and deployment strategies for Canadian organizations that need greater control over AI behaviour, data residency, and model ownership inside [private AI infrastructure](/private-ai).',
    ],

    diagramSteps: ['Corpus preparation', 'Fine-tuning or pre-training', 'Evaluation and quantization', 'Private deployment'],
    sections: [
      {
        title: 'When model training makes sense',
        paragraphs: [
          'Training is not the answer to every AI problem, but it becomes valuable when an organization repeatedly runs into the limits of general-purpose models. If a system keeps misunderstanding terminology, procedures, classifications, or writing standards, adapting the model directly can outperform endless prompt engineering. That is also why [RAG vs Fine-Tuning](/rag-vs-fine-tuning) should be treated as an architecture question, not a slogan.',
          'Training also matters when cost, latency, privacy, or offline deployment requirements make it impractical to rely on large external models for every production task, especially when those models eventually need to run in [MapleOS](/mapleos) or on [MapleNode](/maplenode).',
        ],

        bullets: [
          'Your organization uses specialized terminology',
          'Your workflows require repeatable domain-specific reasoning',
          'Public frontier models make recurring mistakes',
          'You need private, offline, or low-latency deployment',
        ],
      },
      {
        title: 'What CanXP AI training programs cover',
        paragraphs: [
          'CanXP AI supports both adaptation and deeper domain exposure. That includes fine-tuning for behavior and style, continued pre-training for specialized corpora, and packaging work such as quantization or hosted inference preparation.',
          'The goal is not just to produce a checkpoint. It is to help customers build a usable model asset that fits their infrastructure and operational requirements.',
        ],
        bullets: [
          'Fine-tuning and supervised adaptation',
          'Continued pre-training on domain corpora',
          'Quantization for browser, edge, and efficient inference',
          'Private deployment planning and hosted inference options',
        ],
      },
      {
        title: 'Why train small language models',
        paragraphs: [
          'For many enterprise use cases, a smaller model can be easier to control, cheaper to run, and better aligned to the actual task. Small language models are especially useful when the organization wants predictable behavior on a narrow domain rather than general internet-scale breadth. Teams evaluating that path should also review our [small language model training](/small-language-model-training) approach.',
          'That is why the CanXP model story matters strategically. MaplePT and related training services give customers a path toward sovereign, efficient, and specialized intelligence rather than permanent dependence on large foreign APIs. For a deeper editorial view, see [How CanXP AI Trains Models on Specialized Enterprise Knowledge](/insights/how-canxp-ai-trains-models-on-specialized-enterprise-knowledge).',
        ],

      },
      {
        title: 'Training under Canadian control',
        paragraphs: [
          'For Canadian organizations, model training is not only a technical decision. It is also a governance decision. Where the data is prepared, how training artifacts are stored, who can access checkpoints, and where inference eventually runs all affect organizational risk.',
          'CanXP AI positions training within a broader sovereign architecture so customers can connect model adaptation with jurisdiction, privacy, security, and deployment control.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is AI model fine-tuning?',
        answer:
          'Fine-tuning adapts an existing model so it behaves differently on a target task, style, workflow, or domain. It changes model behavior more directly than prompts or retrieval alone.',
      },
      {
        question: 'What is small language model training?',
        answer:
          'Small language model training focuses on compact models that can be adapted for specific enterprise or institutional tasks. These models are often cheaper, more controllable, and easier to deploy privately.',
      },
      {
        question: 'When should an organization train a model instead of using RAG?',
        answer:
          'Organizations should consider training when the model needs to learn terminology, classification patterns, writing standards, or domain-specific reasoning instead of simply looking up facts from documents.',
      },
      {
        question: 'Can CanXP AI train a model on proprietary documents?',
        answer:
          'CanXP AI positions model training around proprietary organizational knowledge and specialized corpora for customers that need private adaptation and deployment control.',
      },
      {
        question: 'Can trained models be deployed privately?',
        answer:
          'Yes. Private deployment is a core reason many organizations pursue compact model training and quantized model packaging.',
      },
    ],
    relatedLinks: [
      '/small-language-model-training',
      '/rag-vs-fine-tuning',
      '/private-ai',
      '/maplept',
      '/healthcare-ai-canada',
      '/legal-ai-canada',
    ],
    ctaTitle: 'Ask CanXP AI about training the right model',
    ctaBody:
      'Whether you need fine-tuning, continued pre-training, or a private deployment plan, CanXP AI can help scope the right training program for your data and use case.',
    ctaHref: '/purchase',
    ctaLabel: 'Explore model training options',
    schemaKinds: ['service'],
  },
  'small-language-model-training': {
    key: 'small-language-model-training',
    path: '/small-language-model-training',
    title: 'Small Language Model Training — Sovereign SLM Development for Canada',
    description:
      'Build and adapt small language models for Canadian enterprise, healthcare, legal, and industrial workflows with CanXP AI.',
    keywords:
      'small language model training, SLM training, sovereign SLM, private SLM, Canadian language model training',
    badge: 'Small Language Models',
    h1: 'Small Language Model Training for Specialized AI Work',
    intro: [
      'Not every organization needs the largest possible model. Many need a model that is cheaper to run, easier to govern, simpler to deploy privately, and better adapted to a narrow domain. That is where small language model training becomes strategically valuable.',
      'CanXP AI works with small language models because they are practical. They can be trained and adapted for specialized workflows, packaged for efficient inference, and deployed in environments where privacy, cost, and control matter as much as raw parameter count.',
    ],
    diagramSteps: ['Compact base model', 'Specialized domain adaptation', 'Efficient packaging', 'Private enterprise deployment'],
    sections: [
      {
        title: 'Why enterprises are looking at SLMs',
        paragraphs: [
          'A compact model can outperform a larger model on a narrow domain when it is trained properly and deployed close to the workflow. That can reduce cost, improve responsiveness, and make governance easier, especially when the model is paired with [private AI infrastructure](/private-ai).',
          'For many Canadian organizations, SLMs are also a sovereignty decision. They offer a more realistic path to Canadian hosting, private deployment, and sector-specific adaptation, including [MapleNode](/maplenode) and browser-native delivery through [MapleOS](/mapleos).',
        ],

      },
      {
        title: 'Where SLM training works best',
        paragraphs: [
          'Small language models are especially useful for classification, drafting, internal assistants, specialized terminology, workflow routing, decision support, and bounded reasoning tasks.',
          'They are not a universal replacement for frontier models. They are a strategic asset for organizations that care about reliability in a defined operating environment.',
        ],
        bullets: [
          'Healthcare process support',
          'Legal document workflows',
          'Industrial operations guidance',
          'Enterprise knowledge assistants',
        ],
      },
      {
        title: 'How MaplePT fits into the story',
        paragraphs: [
          'MaplePT gives CanXP AI a strong foundation for sovereign small language model positioning. It helps separate the model family from the broader platform while creating a clear entity for Canadian-hosted and Canadian-trained SLM narratives.',
          'From an SEO standpoint, that separation matters. Customers can discover MaplePT as a model family, then move naturally into [AI model training](/ai-model-training), [Canadian AI infrastructure](/canadian-ai-infrastructure), and industry-specific deployment pages. The context also connects cleanly to [Why Small Language Models Are Disrupting Enterprise AI](/insights/why-small-language-models-are-disrupting-enterprise-ai).',
        ],

      },
      {
        title: 'From training to deployment',
        paragraphs: [
          'SLM work is most valuable when it does not stop at a checkpoint. CanXP AI connects training to quantization, hosted inference, browser-ready packaging, and private deployment planning so customers can turn a model into an operational system.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is a small language model?',
        answer:
          'A small language model is a more compact model designed for efficient inference and focused deployment. It is often well suited to specialized enterprise or institutional tasks.',
      },
      {
        question: 'Why train a small model instead of using a large frontier model?',
        answer:
          'A small model can be cheaper, more controllable, easier to deploy privately, and better aligned to narrow workflows when it is trained on the right domain data.',
      },
      {
        question: 'Can SLMs be deployed on-premise or at the edge?',
        answer:
          'Yes. Smaller models are often better candidates for private infrastructure, on-premise systems, browser packaging, and edge or restricted-network deployment.',
      },
      {
        question: 'Does CanXP AI support quantized SLM deployment?',
        answer:
          'Yes. Quantization and efficient packaging are part of CanXP AI’s positioning for private and cost-sensitive model deployment.',
      },
    ],
    relatedLinks: [
      '/maplept',
      '/ai-model-training',
      '/rag-vs-fine-tuning',
      '/private-ai',
      '/canadian-ai-infrastructure',
      '/healthcare-ai-canada',
    ],
    ctaTitle: 'Build a sovereign SLM strategy',
    ctaBody:
      'CanXP AI can help evaluate whether a compact, specialized model is the right fit for your knowledge, deployment constraints, and long-term AI ownership goals.',
    ctaHref: '/enterprise',
    ctaLabel: 'Discuss small language model training',
  },
  'private-ai': {
    key: 'private-ai',
    path: '/private-ai',
    title: 'Private AI for Business — Secure Knowledge, Private Deployment, and Control',
    description:
      'Deploy private AI assistants, secure knowledge bases, and confidential AI workflows with CanXP AI under Canadian control.',
    keywords:
      'private AI, private AI for business, secure AI deployment, private AI assistants, confidential AI',
    badge: 'Private AI for Business',
    h1: 'Private AI for Organizations That Need Control',
    intro: [
      'Private AI is not just a hosted chatbot with a nicer access policy. It is an operating model where organizations control where sensitive data lives, how documents are indexed, whether customer knowledge becomes shared training data, and who can access the system.',
      'CanXP AI helps teams move from public AI experimentation to controlled private AI systems built around secure knowledge, role-based access, and deployment options that fit the sensitivity of the work.',
    ],
    diagramSteps: ['Private workspace', 'Secure knowledge base', 'Controlled model access', 'Deployment matched to risk'],
    sections: [
      {
        title: 'What private AI actually means',
        paragraphs: [
          'Private AI means more than hiding a prompt window behind a login. It means the organization can make architectural decisions about storage, embeddings, retention, inference, and administrative boundaries, then surface those controls through [MapleOS](/mapleos) instead of a generic chat box.',
          'That matters when the workflow touches contracts, patient information, internal methods, regulated documents, or strategic operational knowledge, especially if some workloads need to stay closer to the work on [MapleNode](/maplenode).',
        ],

      },
      {
        title: 'Private knowledge bases and assistants',
        paragraphs: [
          'A secure knowledge base lets a team organize confidential documents, internal references, and operational materials without treating those assets as disposable prompt context. That foundation aligns with the thinking in [Why Canadian Organizations Need Private AI Infrastructure](/insights/why-canadian-organizations-need-private-ai-infrastructure).',
          'When paired with the right model and governance approach, a private assistant can deliver better accuracy, stronger traceability, and lower organizational risk than ad hoc public AI use, while still creating a clean path into [AI security](/security) and [AI model training](/ai-model-training).',
        ],

        bullets: [
          'Confidential document retrieval',
          'Department- or client-specific assistants',
          'Role-based access to internal knowledge',
          'Governed AI use inside business workflows',
        ],
      },
      {
        title: 'Why private AI matters in Canada',
        paragraphs: [
          'Canadian organizations increasingly need AI systems that can align with data residency, professional confidentiality, and sector-specific governance obligations. Private AI offers a practical bridge between capability and control.',
          'Instead of banning AI or hoping staff self-govern perfectly, organizations can adopt AI in an environment designed for ownership and oversight.',
        ],
      },
      {
        title: 'CanXP AI as the private AI layer',
        paragraphs: [
          'CanXP AI connects private assistants, secure knowledge bases, model adaptation, and sovereign infrastructure into one enterprise story. That makes private AI easier to understand commercially and easier to position clearly for search and sales.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is private AI?',
        answer:
          'Private AI describes AI systems designed around organizational control over data, access, deployment, and governance rather than uncontrolled use of public consumer AI tools.',
      },
      {
        question: 'How is private AI different from a public AI chatbot?',
        answer:
          'Private AI focuses on confidentiality, knowledge control, access boundaries, and deployment governance. Public tools may not offer the same level of control over data handling or infrastructure.',
      },
      {
        question: 'Can private AI use internal documents safely?',
        answer:
          'Yes. Private AI is often built around secure knowledge bases and controlled retrieval workflows designed for sensitive internal materials.',
      },
      {
        question: 'Who needs private AI first?',
        answer:
          'Professional services, healthcare teams, regulated enterprises, public-sector groups, and organizations with proprietary methods often benefit first from private AI.',
      },
    ],
    relatedLinks: [
      '/data-residency',
      '/pipeda-compliant-ai',
      '/security',
      '/ai-model-training',
      '/legal-ai-canada',
      '/healthcare-ai-canada',
    ],
    ctaTitle: 'Move from public AI experimentation to private AI operations',
    ctaBody:
      'CanXP AI can help scope a private deployment path for teams that need secure knowledge, internal control, and a cleaner governance story.',
    ctaHref: '/enterprise',
    ctaLabel: 'Plan a private AI deployment',
  },
  'pipeda-compliant-ai': {
    key: 'pipeda-compliant-ai',
    path: '/pipeda-compliant-ai',
    title: 'PIPEDA-Compliant AI — Canadian Privacy-Aligned AI for Business',
    description:
      'Understand what PIPEDA-compliant AI means for Canadian businesses and how CanXP AI supports jurisdiction, privacy, and governance-conscious deployment.',
    keywords:
      'PIPEDA-compliant AI, Canadian AI compliance, privacy compliant AI Canada, AI governance Canada',
    badge: 'PIPEDA-Compliant AI',
    h1: 'AI Deployment with Canadian Privacy in Mind',
    intro: [
      'Canadian businesses increasingly ask what PIPEDA-compliant AI actually means in practice. The answer is not a single checkbox. It is a combination of governance, data handling, deployment design, consent practices, access control, and operational transparency.',
      'CanXP AI supports organizations that want a stronger privacy and jurisdiction story for AI adoption. That includes Canadian-hosted options, private knowledge workflows, clearer deployment boundaries, and architecture that reduces unnecessary data movement.',
    ],
    diagramSteps: ['Purpose and governance', 'Controlled data handling', 'Private deployment options', 'Ongoing operational oversight'],
    sections: [
      {
        title: 'What PIPEDA compliance means for AI',
        paragraphs: [
          'PIPEDA applies to how organizations handle personal information in commercial activity. For AI systems, that raises questions about data collection, consent, access, retention, model training, and the use of third-party infrastructure.',
          'An AI deployment that ignores those questions is not made compliant simply by adding policy language after the fact. Compliance requires architecture and operations that support responsible handling from the start.',
        ],
      },
      {
        title: 'Why jurisdiction and deployment matter',
        paragraphs: [
          'If an organization cannot explain where its AI provider stores or processes sensitive information, its privacy story is weak. Jurisdiction matters because it shapes which laws, access risks, and governance controls apply to the system.',
          'That is why CanXP AI emphasizes Canadian control, private deployment options, and secure knowledge workflows as part of a privacy-aligned AI posture.',
        ],
      },
      {
        title: 'Practical steps for Canadian businesses',
        paragraphs: [
          'Organizations adopting AI should evaluate what information enters the system, whether any customer or employee data is retained, what third-party services are involved, and how access is limited internally.',
        ],
        bullets: [
          'Define which data can and cannot enter AI systems',
          'Separate public, internal, and confidential AI use cases',
          'Use private knowledge systems for sensitive workflows',
          'Choose deployment models aligned to risk and jurisdiction',
        ],
      },
      {
        title: 'CanXP AI’s role',
        paragraphs: [
          'CanXP AI does not replace legal advice, but it gives Canadian organizations a stronger technical and operational foundation for privacy-conscious AI adoption. That foundation matters when buyers, executives, regulators, or customers ask how the system is governed.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What does PIPEDA-compliant AI mean?',
        answer:
          'PIPEDA-compliant AI means AI deployment designed with privacy obligations in mind, including how personal information is collected, processed, retained, secured, and governed in commercial contexts.',
      },
      {
        question: 'Is Canadian hosting enough by itself?',
        answer:
          'No. Canadian hosting helps, but compliance also depends on governance, data practices, access control, vendor relationships, and how the AI system is actually used.',
      },
      {
        question: 'Why do businesses ask about jurisdiction for AI?',
        answer:
          'Jurisdiction affects legal exposure, data handling expectations, and the organization’s ability to explain how sensitive information moves through an AI system.',
      },
      {
        question: 'Can private AI help reduce compliance risk?',
        answer:
          'Yes. Private AI can support stronger governance by limiting exposure, improving access control, and avoiding unnecessary use of public consumer AI systems for sensitive work.',
      },
    ],
    relatedLinks: ['/private-ai', '/data-residency', '/security', '/compliance', '/healthcare-ai-canada', '/legal-ai-canada'],
    ctaTitle: 'Build an AI strategy that supports Canadian privacy obligations',
    ctaBody:
      'CanXP AI can help you evaluate the right mix of private deployment, knowledge controls, and jurisdiction-aware architecture for Canadian business use.',
    ctaHref: '/enterprise',
    ctaLabel: 'Discuss a privacy-aligned AI architecture',
  },
  'healthcare-ai-canada': {
    key: 'healthcare-ai-canada',
    path: '/healthcare-ai-canada',
    title: 'Healthcare AI Canada — Private, Sovereign AI for Clinical and Regional Workflows',
    description:
      'CanXP AI supports healthcare AI in Canada with private knowledge systems, regional governance alignment, and sovereign deployment options.',
    keywords:
      'healthcare AI Canada, medical AI Canada, clinical AI Canada, sovereign healthcare AI, private healthcare AI',
    badge: 'Healthcare AI Canada',
    h1: 'Healthcare AI for Canadian Clinical and Regional Realities',
    intro: [
      'Healthcare AI in Canada is not just about model quality. It is about governance, data handling, regional accountability, and clinical trust. The more sensitive the workflow, the more important local jurisdiction and controlled deployment become.',
      'CanXP AI supports healthcare organizations that need private AI systems, secure knowledge bases, and deployment options that respect provincial, institutional, and regional requirements.',
    ],
    diagramSteps: ['Clinical knowledge control', 'Regional governance alignment', 'Private AI workflows', 'Deployment matched to sensitivity'],
    sections: [
      {
        title: 'Why healthcare AI needs local jurisdiction',
        paragraphs: [
          'Canadian healthcare is not governed in a single uniform way. Hospitals, health authorities, clinics, research organizations, and provincial systems often operate under different policies and approval structures.',
          'That means healthcare AI must be adaptable not only to the task, but also to the governance environment in which it is deployed. The issue is explored in more detail in [Why Healthcare AI Requires Provincial and Regional Data Governance](/insights/healthcare-ai-provincial-regional-data-governance).',
        ],

      },
      {
        title: 'What private AI changes in clinical environments',
        paragraphs: [
          'Private AI can reduce the pressure to push sensitive workflows through public AI interfaces. It allows clinical organizations to think in terms of controlled knowledge systems, internal access, and bounded deployment models through [private AI infrastructure](/private-ai).',
          'That can support better documentation assistance, clinical workflow support, policy retrieval, and specialized domain adaptation without treating every workflow like a consumer chatbot use case. When local deployment matters, [MapleNode](/maplenode) becomes part of that story.',
        ],

      },
      {
        title: 'Model training for healthcare methods',
        paragraphs: [
          'Healthcare organizations often operate with specialized terminology, local clinical practices, and internal procedural differences. Training or adapting a small model can be useful when generic models repeatedly miss that context, which is why [AI model training](/ai-model-training) and [small language model training](/small-language-model-training) are important healthcare deployment paths.',
        ],

        bullets: [
          'Specialized terminology and classification support',
          'Department-specific assistants and workflows',
          'Private knowledge retrieval for policy and procedures',
          'Regional deployment aligned to local operating constraints',
        ],
      },
      {
        title: 'CanXP AI’s healthcare positioning',
        paragraphs: [
          'CanXP AI gives healthcare organizations a Canadian story for private AI, sovereign model training, and deployment design. That story is especially valuable for teams that need local trust, controlled data handling, and a more credible governance posture.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Why does healthcare AI need local jurisdiction?',
        answer:
          'Healthcare AI needs local jurisdiction because sensitive health information, clinical workflows, and public accountability requirements make data handling and governance especially important.',
      },
      {
        question: 'Can AI be trained on specialized clinical methods?',
        answer:
          'Yes. Model adaptation can help when specialized terminology, procedures, or local operating methods are central to the workflow.',
      },
      {
        question: 'How can private AI reduce risk in medical workflows?',
        answer:
          'Private AI can reduce risk by improving control over documents, access, residency, deployment, and system boundaries compared with uncontrolled public-tool use.',
      },
      {
        question: 'Can CanXP AI support provincial or regional healthcare requirements?',
        answer:
          'CanXP AI positions its healthcare offering around Canadian jurisdiction and deployment flexibility so organizations can align systems to regional requirements more effectively.',
      },
    ],
    relatedLinks: ['/ai-model-training', '/small-language-model-training', '/private-ai', '/data-residency', '/pipeda-compliant-ai', '/maplept'],
    ctaTitle: 'Explore a private healthcare AI deployment',
    ctaBody:
      'If your healthcare organization needs AI that fits Canadian and regional governance realities, CanXP AI can help scope a safer deployment model.',
    ctaHref: '/enterprise',
    ctaLabel: 'Talk about healthcare AI',
  },
  'legal-ai-canada': {
    key: 'legal-ai-canada',
    path: '/legal-ai-canada',
    title: 'Legal AI Canada — Private AI for Law Firms, Legal Ops, and Confidential Work',
    description:
      'Deploy legal AI in Canada with stronger confidentiality, data residency, and private knowledge controls through CanXP AI.',
    keywords:
      'legal AI Canada, law firm AI Canada, private legal AI, confidential AI for legal work',
    badge: 'Legal AI Canada',
    h1: 'Legal AI That Respects Confidentiality and Jurisdiction',
    intro: [
      'Law firms and legal teams cannot treat AI like a casual productivity add-on. Confidential documents, client trust, internal methods, and jurisdictional questions make legal AI a governance issue as much as a capability issue.',
      'CanXP AI supports legal AI deployment with private assistants, secure knowledge bases, and model training options for firms that want more control over how AI interacts with sensitive legal work.',
    ],
    diagramSteps: ['Confidential legal knowledge', 'Firm-specific controls', 'Private AI workflows', 'Canadian jurisdiction'],
    sections: [
      {
        title: 'Why jurisdiction matters in legal AI',
        paragraphs: [
          'Legal work depends on confidentiality, defensible process, and trust. If a firm cannot clearly explain where information flows, who controls the infrastructure, or how retention works, the AI strategy may create more concern than value.',
          'Canadian jurisdiction can strengthen that story by making deployment and governance easier to reason about in a Canadian legal and professional context.',
        ],
      },
      {
        title: 'Private AI for legal methods',
        paragraphs: [
          'Many legal workflows are repeatable and method-driven. Firms have internal drafting styles, document practices, review approaches, and matter-specific knowledge that generic public models do not truly understand.',
          'That makes private knowledge systems and model adaptation especially attractive for legal use cases.',
        ],
        bullets: [
          'Firm-specific drafting assistance',
          'Secure retrieval across precedents and internal materials',
          'Matter-type classification and intake support',
          'Controlled AI use for legal operations teams',
        ],
      },
      {
        title: 'How private AI differs from public AI tools',
        paragraphs: [
          'Public tools may be useful for low-risk experimentation, but private AI is designed around stronger control over data, access, deployment, and knowledge handling. That distinction matters more in legal environments than almost anywhere else.',
        ],
      },
      {
        title: 'A Canadian positioning for law firms',
        paragraphs: [
          'CanXP AI gives firms a stronger local positioning around private AI, secure knowledge, and sovereign deployment. That is valuable not only for internal confidence, but also for explaining AI choices to clients and stakeholders.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Why should law firms care about AI data residency?',
        answer:
          'Data residency matters because legal teams handle confidential documents and need a clear story about where sensitive information is processed and governed.',
      },
      {
        question: 'Can legal AI be trained on firm-specific methods?',
        answer:
          'Yes. Model adaptation can help when a firm wants AI that better follows its internal methods, language, and workflow expectations.',
      },
      {
        question: 'How is private AI different from public AI tools?',
        answer:
          'Private AI focuses on controlled knowledge handling, deployment, access, and governance rather than open-ended use of public AI interfaces.',
      },
      {
        question: 'Can CanXP AI help protect confidential legal documents?',
        answer:
          'CanXP AI supports private knowledge and deployment models designed for organizations that need stronger confidentiality and jurisdiction control.',
      },
    ],
    relatedLinks: ['/ai-model-training', '/private-ai', '/pipeda-compliant-ai', '/data-residency', '/maplept', '/sovereign-ai-canada'],
    ctaTitle: 'Design a legal AI workflow you can defend',
    ctaBody:
      'CanXP AI can help firms and legal teams scope a private AI architecture that aligns better with confidentiality, jurisdiction, and internal control requirements.',
    ctaHref: '/enterprise',
    ctaLabel: 'Discuss legal AI deployment',
  },
  'canadian-ai-infrastructure': {
    key: 'canadian-ai-infrastructure',
    path: '/canadian-ai-infrastructure',
    title: 'Canadian AI Infrastructure — Sovereign Compute, Hosting, and Deployment Control',
    description:
      'Learn how CanXP AI approaches Canadian AI infrastructure, sovereign compute, and private deployment for regulated and enterprise workloads.',
    keywords:
      'Canadian AI infrastructure, sovereign compute Canada, Canadian AI hosting, private AI infrastructure',
    badge: 'Canadian AI Infrastructure',
    h1: 'Canadian AI Infrastructure for Controlled Deployment',
    intro: [
      'AI adoption is increasingly constrained by infrastructure, not just software. Organizations may have a useful model and a promising workflow, but still lack a credible story around where systems run, who controls the stack, and how sensitive workloads are separated from general-purpose cloud dependency.',
      'CanXP AI positions infrastructure as a first-class part of the sovereign AI conversation: Canadian hosting, controlled deployment, and system design aligned to privacy, security, and operational ownership.',
    ],
    diagramSteps: ['Canadian hosting posture', 'Model orchestration', 'Private workload separation', 'Sector-specific deployment'],
    sections: [
      {
        title: 'Why infrastructure is now a buying issue',
        paragraphs: [
          'As organizations move beyond experimentation, they start asking operational questions. Where does inference run? Where are embeddings stored? Can a system be isolated for a specific tenant or restricted environment? Can it support regulated workloads without awkward architectural compromises?',
          'Those are infrastructure questions, and they are central to enterprise AI procurement. They also explain why [MapleNode](/maplenode) and [private AI](/private-ai) belong in the same conversation.',
        ],

      },
      {
        title: 'What sovereign compute really means',
        paragraphs: [
          'Sovereign compute is not simply about putting a server in Canada. It is about preserving operational control over hosting, model serving, data movement, access boundaries, and deployment architecture.',
          'That broader view is what makes sovereign compute commercially meaningful for Canadian organizations.',
        ],
      },
      {
        title: 'CanXP AI’s infrastructure story',
        paragraphs: [
          'CanXP AI connects platform, model, and infrastructure positioning so that organizations can evaluate the full stack together. That supports a clearer story for public-sector, healthcare, legal, enterprise, and industrial customers, from [MapleOS](/mapleos) at the operating layer to [AI model training](/ai-model-training) in the specialization layer.',
        ],

        bullets: [
          'Canadian-hosted deployment options',
          'Private and dedicated environment design',
          'Hosted inference for sovereign model programs',
          'Architecture support for controlled rollout',
        ],
      },
      {
        title: 'Reducing foreign platform dependency',
        paragraphs: [
          'A Canadian AI infrastructure strategy is also a resilience strategy. It reduces overreliance on foreign platforms for critical workflows and creates space for local control, local innovation, and sector-specific deployment planning. For the buyer-side framing, see [How Canadian AI Infrastructure Can Reduce Foreign Platform Dependency](/insights/canadian-ai-infrastructure-foreign-platform-dependency).',
        ],

      },
    ],
    faqs: [
      {
        question: 'What is Canadian AI infrastructure?',
        answer:
          'Canadian AI infrastructure refers to AI hosting, compute, deployment, and operational systems aligned to Canadian control, jurisdiction, and organizational governance needs.',
      },
      {
        question: 'Is sovereign compute the same as Canadian hosting?',
        answer:
          'Canadian hosting is part of the answer, but sovereign compute also includes who controls the infrastructure, how data moves, and how the deployment is governed over time.',
      },
      {
        question: 'Why does infrastructure matter for AI buyers?',
        answer:
          'Infrastructure matters because buyers need a credible story about data handling, model serving, isolation, security, and long-term operational control.',
      },
      {
        question: 'Can CanXP AI support private deployment environments?',
        answer:
          'Yes. Private, dedicated, and governance-conscious deployment is central to CanXP AI’s sovereign positioning.',
      },
    ],
    relatedLinks: ['/sovereign-ai-canada', '/private-ai', '/data-residency', '/enterprise-ai', '/maplept', '/purchase'],
    ctaTitle: 'Scope the right sovereign infrastructure path',
    ctaBody:
      'CanXP AI can help your team connect model, platform, and infrastructure decisions into a deployment plan that fits Canadian operational realities.',
    ctaHref: '/enterprise',
    ctaLabel: 'Discuss Canadian AI infrastructure',
  },
  'rag-vs-fine-tuning': {
    key: 'rag-vs-fine-tuning',
    path: '/rag-vs-fine-tuning',
    title: 'RAG vs Fine-Tuning — When Should You Train an AI Model?',
    description:
      'Understand when retrieval-augmented generation, fine-tuning, or pre-training is the right approach for specialized enterprise AI systems.',
    keywords:
      'RAG vs fine-tuning, when to fine tune a model, enterprise AI training, pre-training vs RAG',
    badge: 'RAG vs Fine-Tuning',
    h1: 'RAG Helps AI Find Knowledge. Fine-Tuning Helps AI Learn Behaviour.',
    intro: [
      'Organizations evaluating AI often ask whether they should use retrieval-augmented generation, fine-tuning, or some form of pre-training. The honest answer is that each approach solves a different problem.',
      'CanXP AI helps organizations decide when they need document access, when they need behaviour change, and when the domain is deep enough that the model needs more foundational exposure to the corpus itself.',
    ],
    diagramSteps: ['RAG for document access', 'Fine-tuning for behaviour', 'Pre-training for domain depth', 'Deployment matched to use case'],
    sections: [
      {
        title: 'When RAG is the right tool',
        paragraphs: [
          'RAG is useful when the model already has strong general reasoning ability and primarily needs access to the right documents at the right time. Policies, manuals, product documentation, and reference materials often fit well here.',
          'RAG does not fundamentally retrain the model. It gives the model better access to relevant context.',
        ],
      },
      {
        title: 'When fine-tuning is the better answer',
        paragraphs: [
          'Fine-tuning matters when the model needs to change how it writes, classifies, follows procedures, interprets domain terminology, or behaves in repeated task patterns. It is not about finding facts. It is about altering model behaviour.',
        ],
        bullets: [
          'Consistent output format or style',
          'Specialized classifications',
          'Domain-specific terminology handling',
          'Procedure-following and workflow alignment',
        ],
      },
      {
        title: 'When continued pre-training makes sense',
        paragraphs: [
          'Pre-training or continued pre-training becomes useful when the domain is deep enough that the model needs broader exposure to a specialized corpus before fine-tuning can be effective. This is common in scientific, industrial, legal, and medical environments with large specialized text bodies.',
        ],
      },
      {
        title: 'How CanXP AI helps decide',
        paragraphs: [
          'The best approach is often a layered one. A private knowledge base can support retrieval, while a smaller model is fine-tuned for consistent behaviour on the organization’s core task. CanXP AI helps customers choose the minimum complexity required for reliable performance.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is RAG?',
        answer:
          'RAG, or retrieval-augmented generation, gives a model access to relevant documents during inference so it can answer with current or organization-specific information.',
      },
      {
        question: 'What is fine-tuning?',
        answer:
          'Fine-tuning adapts a model’s behaviour so it performs a task differently, follows a style, or handles specialized reasoning more reliably.',
      },
      {
        question: 'When should an organization choose RAG over fine-tuning?',
        answer:
          'Choose RAG when the main problem is document access. Choose fine-tuning when the model needs to learn a new behavior, format, style, or domain-specific task pattern.',
      },
      {
        question: 'Can an organization use both RAG and fine-tuning?',
        answer:
          'Yes. Many strong enterprise systems combine fine-tuned behavior with a private retrieval layer for up-to-date or organization-specific documents.',
      },
    ],
    relatedLinks: ['/ai-model-training', '/small-language-model-training', '/private-ai', '/maplept', '/healthcare-ai-canada', '/legal-ai-canada'],
    ctaTitle: 'Need help choosing the right approach?',
    ctaBody:
      'CanXP AI can help assess whether your workflow needs retrieval, adaptation, continued pre-training, or a combination of approaches.',
    ctaHref: '/enterprise',
    ctaLabel: 'Ask which approach fits your organization',
    schemaKinds: ['service'],
  },
  'data-residency': {
    key: 'data-residency',
    path: '/data-residency',
    title: 'Canadian AI Data Residency — Keep Sensitive Knowledge Under Canadian Jurisdiction',
    description:
      'Understand Canadian AI data residency, where sensitive knowledge should live, and how CanXP AI supports jurisdiction-aware deployment.',
    keywords: 'AI data residency Canada, Canadian data residency AI, Canadian jurisdiction data, private AI hosting Canada',
    badge: 'Data Residency',
    h1: 'Canadian AI Data Residency for Sensitive Workflows',
    intro: [
      'Data residency is one of the first questions serious buyers ask about AI. Where are documents stored? Where are embeddings created? Where does inference happen? Can a team explain those answers to customers, leadership, and regulators?',
      'CanXP AI approaches data residency as part of a broader sovereign AI architecture, helping organizations keep sensitive knowledge under Canadian jurisdiction when that matters to the deployment.',
    ],
    diagramSteps: ['Document storage', 'Knowledge indexing', 'Inference location', 'Governance and retention'],
    sections: [
      {
        title: 'Why data residency matters',
        paragraphs: [
          'Residency matters because it helps organizations understand where sensitive information lives and which legal environment governs the infrastructure handling it.',
          'That clarity becomes especially important for healthcare, legal, public-sector, research, and enterprise teams with internal confidentiality obligations.',
        ],
      },
      {
        title: 'Residency is bigger than file storage',
        paragraphs: [
          'Organizations should think beyond where raw files are uploaded. AI systems may also store embeddings, caches, logs, checkpoints, outputs, and related metadata. A strong residency posture looks at the whole pipeline.',
        ],
      },
      {
        title: 'Questions buyers should ask',
        paragraphs: ['A serious AI residency review should ask:'],
        bullets: [
          'Where are source documents stored?',
          'Where are embeddings and indexes stored?',
          'Where does model inference run?',
          'Who can access stored data and artifacts?',
          'How are deletion and retention handled?',
        ],
      },
      {
        title: 'CanXP AI’s approach',
        paragraphs: [
          'CanXP AI connects residency to private deployment, secure knowledge bases, and Canadian infrastructure positioning. That makes data residency part of a coherent trust story rather than a standalone marketing claim.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is AI data residency?',
        answer:
          'AI data residency refers to where data and related AI artifacts are stored and processed, including documents, embeddings, outputs, logs, and model-serving infrastructure.',
      },
      {
        question: 'Why does data residency matter in Canada?',
        answer:
          'It matters because organizations often need a clearer story about jurisdiction, governance, and how sensitive information is handled through the AI lifecycle.',
      },
      {
        question: 'Is file storage the only residency issue?',
        answer:
          'No. Organizations should also consider embeddings, logs, inference endpoints, model artifacts, and retention policies.',
      },
      {
        question: 'Can private AI improve residency control?',
        answer:
          'Yes. Private AI can make residency easier to reason about because it allows tighter control over where documents, models, and workflows are hosted.',
      },
    ],
    relatedLinks: ['/sovereign-ai-canada', '/private-ai', '/pipeda-compliant-ai', '/security', '/compliance', '/canadian-ai-infrastructure'],
    ctaTitle: 'Clarify your AI data residency posture',
    ctaBody:
      'CanXP AI can help identify the right residency model for confidential, regulated, or mission-critical AI workloads.',
    ctaHref: '/enterprise',
    ctaLabel: 'Review your data residency options',
  },
  security: {
    key: 'security',
    path: '/security',
    title: 'CanXP AI Security — Private AI Infrastructure, Encryption & Enterprise Controls',
    description:
      'Review CanXP AI’s security positioning for private AI infrastructure, enterprise controls, secure knowledge systems, and governed deployment.',
    keywords: 'CanXP AI security, private AI security, AI infrastructure security, secure knowledge base AI',
    badge: 'Security',
    h1: 'Security for Private AI and Sovereign Deployments',
    intro: [
      'Security is not optional in enterprise AI. Organizations need to understand how documents are protected, how access is limited, where infrastructure is operated, and how deployments can be designed for stronger control.',
      'CanXP AI positions security as part of the broader sovereign AI stack, connecting private knowledge, controlled deployment, and enterprise architecture requirements.',
    ],
    diagramSteps: ['Access boundaries', 'Encrypted systems', 'Private knowledge control', 'Enterprise deployment design'],
    sections: [
      {
        title: 'Security starts with architecture',
        paragraphs: [
          'The strongest AI security posture usually begins with design choices: which systems are public, which are private, how access is segmented, and where sensitive knowledge is allowed to flow.',
          'That is why private deployment and data residency are so closely tied to the security conversation.',
        ],
      },
      {
        title: 'What buyers expect',
        paragraphs: ['Enterprise buyers typically want clarity on:'],
        bullets: [
          'Encryption and secure transport',
          'Role-based access and administrative boundaries',
          'Private tenant or dedicated deployment options',
          'Auditability and operational visibility',
        ],
      },
      {
        title: 'Security for secure knowledge bases',
        paragraphs: [
          'Knowledge systems are a major risk surface in AI deployments because they often contain the most sensitive documents in the organization. Securing those systems is central to any credible private AI program.',
        ],
      },
      {
        title: 'How CanXP AI frames security',
        paragraphs: [
          'CanXP AI presents security alongside jurisdiction, compliance, and infrastructure so customers can evaluate enterprise readiness more holistically.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Why does private AI security matter?',
        answer:
          'Private AI security matters because organizations need stronger control over where sensitive information flows and who can access AI systems that interact with confidential knowledge.',
      },
      {
        question: 'Does AI security only mean encryption?',
        answer:
          'No. Encryption is important, but security also includes architecture, access control, isolation, monitoring, and deployment design.',
      },
      {
        question: 'Why are secure knowledge bases important?',
        answer:
          'They are important because many AI systems derive value from proprietary documents and internal references, which need stronger protection than generic public content.',
      },
      {
        question: 'Can CanXP AI support enterprise security expectations?',
        answer:
          'Yes. CanXP AI positions its sovereign and private offerings around enterprise control, secure deployment, and governed knowledge workflows.',
      },
    ],
    relatedLinks: ['/private-ai', '/data-residency', '/compliance', '/pipeda-compliant-ai', '/canadian-ai-infrastructure', '/enterprise-ai'],
    ctaTitle: 'Review your AI security architecture',
    ctaBody:
      'CanXP AI can help you evaluate whether your AI deployment model supports the level of control your organization actually needs.',
    ctaHref: '/enterprise',
    ctaLabel: 'Discuss AI security controls',
  },
  compliance: {
    key: 'compliance',
    path: '/compliance',
    title: 'AI Compliance in Canada — Governance, Jurisdiction, and Controlled Deployment',
    description:
      'Understand how CanXP AI supports governance-conscious AI deployment for Canadian organizations managing privacy, residency, and compliance-sensitive workflows.',
    keywords: 'AI compliance Canada, Canadian AI governance, regulated AI deployment Canada, compliance sensitive AI',
    badge: 'Compliance',
    h1: 'AI Compliance Requires More Than Policy Language',
    intro: [
      'Compliance-sensitive AI is not achieved by adding a disclaimer after deployment. It depends on how the system is architected, which data enters it, where it runs, and how the organization governs use over time.',
      'CanXP AI helps organizations approach compliance through private deployment, stronger residency posture, clearer jurisdiction, and model strategies aligned to regulated environments.',
    ],
    diagramSteps: ['Governance boundaries', 'Jurisdiction clarity', 'Controlled workflows', 'Ongoing oversight'],
    sections: [
      {
        title: 'What compliance-sensitive AI teams need',
        paragraphs: [
          'Most compliance-sensitive buyers need practical answers, not vague assurances. They want to know how AI will be used, what information can enter the system, where it is hosted, and how access is managed.',
        ],
      },
      {
        title: 'Why infrastructure and model choices affect compliance',
        paragraphs: [
          'A highly sensitive workflow may need a private knowledge layer, a fine-tuned smaller model, or a dedicated deployment environment instead of a general public AI interface. The technical architecture shapes the compliance posture.',
        ],
      },
      {
        title: 'Operational controls matter',
        paragraphs: ['A stronger compliance posture usually depends on controls such as:'],
        bullets: [
          'Data classification for AI use',
          'Access boundaries and role controls',
          'Retention and deletion rules',
          'Private deployment for higher-risk workflows',
        ],
      },
      {
        title: 'How CanXP AI supports the conversation',
        paragraphs: [
          'CanXP AI gives Canadian organizations a more credible trust story by connecting compliance to sovereignty, security, private AI, and deployment architecture.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is compliance-sensitive AI?',
        answer:
          'Compliance-sensitive AI refers to AI deployment in environments where privacy, confidentiality, regulation, governance, or institutional oversight requirements materially affect system design and operation.',
      },
      {
        question: 'Can compliance be solved with a policy alone?',
        answer:
          'No. Policies matter, but compliance also depends on architecture, deployment choices, access control, data handling, and ongoing operational governance.',
      },
      {
        question: 'Why do smaller private models sometimes help with compliance?',
        answer:
          'Smaller private models can be easier to control, cheaper to isolate, and better suited to dedicated or restricted deployment environments.',
      },
      {
        question: 'Can CanXP AI support regulated organizations?',
        answer:
          'Yes. CanXP AI positions its platform, training, and deployment model for organizations that need stronger control over residency, privacy, and governance.',
      },
    ],
    relatedLinks: ['/pipeda-compliant-ai', '/data-residency', '/security', '/private-ai', '/sovereign-ai-canada', '/healthcare-ai-canada'],
    ctaTitle: 'Build a stronger AI governance posture',
    ctaBody:
      'CanXP AI can help connect platform choices, model strategy, and deployment architecture into a compliance-aware rollout plan.',
    ctaHref: '/enterprise',
    ctaLabel: 'Talk about compliance-sensitive AI',
  },
  'canadian-ai-platform': {
    key: 'canadian-ai-platform',
    path: '/canadian-ai-platform',
    title: 'Canadian AI Platform — Private Assistants, Secure Knowledge, and Sovereign Workflows',
    description:
      'Discover the CanXP AI platform for private assistants, secure knowledge bases, sovereign workflows, and Canadian-hosted AI operations.',
    keywords: 'Canadian AI platform, sovereign AI platform Canada, private AI platform, enterprise AI platform Canada',
    badge: 'Canadian AI Platform',
    h1: 'A Canadian AI Platform Built for Control and Deployment',
    intro: [
      'CanXP AI is more than a single model or assistant. It is a Canadian AI platform that connects private AI workspaces, secure knowledge systems, sovereign model access, and controlled deployment options for serious organizational use.',
      'That platform framing is important because buyers need to understand CanXP AI as a category-level offering, not just as a launch announcement or a collection of disconnected product claims.',
    ],
    diagramSteps: ['Private assistants', 'Secure knowledge bases', 'Model orchestration', 'Sovereign deployment'],
    sections: [
      {
        title: 'What makes a platform different',
        paragraphs: [
          'A platform gives organizations a repeatable environment for operating AI across teams and workflows. It creates a layer where models, knowledge, governance, and deployment can work together instead of living in isolated tools.',
        ],
      },
      {
        title: 'CanXP AI platform layers',
        paragraphs: [
          'The CanXP AI platform story includes the core platform itself, [MapleOS](/mapleos) as the operating environment, [MapleNode](/maplenode) as the edge deployment path, MaplePT as the model family, and training and infrastructure services for organizations that need deeper control.',
        ],

        bullets: [
          'CanXP AI Platform',
          'MapleOS product layer',
          'MaplePT model family',
          'Private AI and model training services',
        ],
      },
      {
        title: 'Why Canadian platform identity matters',
        paragraphs: [
          'Canadian buyers increasingly search for a local AI platform that can support privacy, governance, and deployment under Canadian jurisdiction. A strong category-led platform page helps CanXP compete for that intent directly while still guiding readers toward [Canadian sovereign AI](/sovereign-ai-canada) and [private AI infrastructure](/private-ai).',
        ],

      },
      {
        title: 'Built for organizations, not casual experimentation',
        paragraphs: [
          'The CanXP platform story is strongest when it emphasizes enterprise trust, sovereign AI demand, model training demand, and Canadian infrastructure demand rather than generic consumer AI language.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is a Canadian AI platform?',
        answer:
          'A Canadian AI platform is an AI operating environment designed around Canadian buyers, Canadian jurisdiction, and deployment models that support stronger local control.',
      },
      {
        question: 'How is CanXP AI different from a single AI model?',
        answer:
          'CanXP AI combines platform capabilities, private knowledge systems, sovereign models, training services, and deployment architecture rather than offering only a standalone model.',
      },
      {
        question: 'Does the CanXP AI platform support private assistants and knowledge bases?',
        answer:
          'Yes. Private assistants and secure knowledge systems are part of the core platform story.',
      },
      {
        question: 'Where do MapleOS and MaplePT fit?',
        answer:
          'MapleOS is a product layer and MaplePT is the sovereign model family, both positioned within the broader CanXP AI platform architecture.',
      },
    ],
    relatedLinks: ['/sovereign-ai-canada', '/private-ai', '/mapleos', '/maplept', '/ai-model-training', '/canadian-ai-infrastructure'],
    ctaTitle: 'See how the CanXP platform fits your AI program',
    ctaBody:
      'CanXP AI can help you connect platform, model, training, and infrastructure decisions into one Canadian deployment strategy.',
    ctaHref: '/enterprise',
    ctaLabel: 'Discuss the CanXP AI platform',
    schemaKinds: ['software'],
  },
  'enterprise-ai': {
    key: 'enterprise-ai',
    path: '/enterprise-ai',
    title: 'Enterprise AI Canada — Private Deployment, Secure Knowledge, and Custom Model Programs',
    description:
      'CanXP AI helps Canadian enterprises deploy private AI, secure knowledge systems, and custom model training programs under stronger operational control.',
    keywords: 'enterprise AI Canada, private enterprise AI, enterprise AI training, Canadian enterprise AI platform',
    badge: 'Enterprise AI Canada',
    h1: 'Enterprise AI for Organizations That Need More Than a Demo',
    intro: [
      'Enterprise AI buyers do not only evaluate model quality. They evaluate risk, governance, cost, deployment, integration, and the long-term implications of putting business knowledge into a system they do not fully control.',
      'CanXP AI helps Canadian organizations move from AI experimentation to enterprise deployment with private assistants, secure knowledge bases, sovereign infrastructure, and model training programs tailored to specialized work.',
    ],
    diagramSteps: ['Enterprise knowledge', 'Governed AI workflows', 'Model adaptation', 'Private deployment'],
    sections: [
      {
        title: 'Why enterprise AI requires a different stack',
        paragraphs: [
          'An enterprise AI program needs repeatability, controls, and a deployment story that can survive procurement, risk review, and operational rollout. Consumer AI tools rarely provide that by default.',
        ],
      },
      {
        title: 'Common enterprise requirements',
        paragraphs: ['Enterprise teams often need:'],
        bullets: [
          'Private deployment or controlled hosting',
          'Secure knowledge access across departments',
          'Custom model adaptation for internal methods',
          'Pricing and infrastructure that scale predictably',
        ],
      },
      {
        title: 'How CanXP AI helps enterprises',
        paragraphs: [
          'CanXP AI connects Canadian platform identity, sovereign deployment, private AI, and model training into one enterprise narrative. That is especially powerful for sectors where trust and control are part of the purchasing process.',
        ],
      },
      {
        title: 'Enterprise value beyond chat',
        paragraphs: [
          'The strongest enterprise deployments use AI for knowledge operations, classification, drafting, workflow support, internal copilots, and specialized model programs — not just generic chat access. CanXP AI is positioned for that broader scope.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What does enterprise AI mean in practice?',
        answer:
          'Enterprise AI means AI systems built for repeatable business use with stronger controls around access, knowledge, governance, deployment, and integration.',
      },
      {
        question: 'Why do enterprises ask for private AI?',
        answer:
          'Enterprises ask for private AI because they need stronger control over confidential data, system behavior, and deployment architecture.',
      },
      {
        question: 'Can enterprise AI include custom model training?',
        answer:
          'Yes. Many enterprise programs include fine-tuning or continued pre-training when general-purpose models do not adequately reflect internal terminology or workflows.',
      },
      {
        question: 'How does CanXP AI support enterprise deployment?',
        answer:
          'CanXP AI supports enterprise deployment through private AI systems, sovereign infrastructure positioning, secure knowledge bases, and Canadian model training services.',
      },
    ],
    relatedLinks: ['/canadian-ai-platform', '/sovereign-ai-canada', '/private-ai', '/ai-model-training', '/canadian-ai-infrastructure', '/purchase'],
    ctaTitle: 'Turn AI interest into enterprise deployment',
    ctaBody:
      'CanXP AI can help scope an enterprise AI architecture that fits your organization’s knowledge, governance, and deployment constraints.',
    ctaHref: '/enterprise',
    ctaLabel: 'Talk about enterprise AI',
  },
};

const seoPageEditorialMetadata: Record<SeoPageKey, EditorialLinkMetadata> = {
  'sovereign-ai-canada': buildEditorialLinkMetadata({
    cluster: 'sovereignAiCanadianInfrastructure',
    priorityDestinations: ['/sovereign-ai-canada', '/mapleos', '/private-ai', '/canadian-ai-infrastructure', '/ai-model-training'],
    suggestedInBodyLinks: [
      { anchor: 'MapleOS AI Operating System', href: '/mapleos', kind: 'product' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'concept' },
      { anchor: 'Canadian AI infrastructure', href: '/canadian-ai-infrastructure', kind: 'concept' },
      { anchor: 'AI model training', href: '/ai-model-training', kind: 'commercial' },
      { anchor: 'What Is Sovereign AI and Why Does Canada Need It?', href: '/insights/what-is-sovereign-ai-canada', kind: 'insight' },
    ],
    commercialDestination: '/enterprise',
  }),
  'ai-model-training': buildEditorialLinkMetadata({
    cluster: 'slmModelTraining',
    priorityDestinations: ['/ai-model-training', '/small-language-model-training', '/mapleos', '/private-ai', '/rag-vs-fine-tuning'],
    suggestedInBodyLinks: [
      { anchor: 'small language model training', href: '/small-language-model-training', kind: 'product' },
      { anchor: 'MapleOS AI Operating System', href: '/mapleos', kind: 'product' },
      { anchor: 'RAG vs fine-tuning', href: '/rag-vs-fine-tuning', kind: 'concept' },
      { anchor: 'How CanXP AI Trains Models on Specialized Enterprise Knowledge', href: '/insights/how-canxp-ai-trains-models-on-specialized-enterprise-knowledge', kind: 'insight' },
    ],
    commercialDestination: '/purchase',
  }),
  'small-language-model-training': buildEditorialLinkMetadata({
    cluster: 'slmModelTraining',
    priorityDestinations: ['/small-language-model-training', '/ai-model-training', '/maplenode', '/private-ai', '/maplept'],
    suggestedInBodyLinks: [
      { anchor: 'AI model training', href: '/ai-model-training', kind: 'product' },
      { anchor: 'MapleNode edge AI appliance', href: '/maplenode', kind: 'product' },
      { anchor: 'Why Small Language Models Are Disrupting Enterprise AI', href: '/insights/why-small-language-models-are-disrupting-enterprise-ai', kind: 'insight' },
      { anchor: 'MaplePT', href: '/maplept', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
  }),
  'private-ai': buildEditorialLinkMetadata({
    cluster: 'privateAiInfrastructure',
    priorityDestinations: ['/private-ai', '/mapleos', '/maplenode', '/security', '/sovereign-ai-canada'],
    suggestedInBodyLinks: [
      { anchor: 'MapleOS AI Operating System', href: '/mapleos', kind: 'product' },
      { anchor: 'MapleNode edge AI appliance', href: '/maplenode', kind: 'product' },
      { anchor: 'Why Canadian Organizations Need Private AI Infrastructure', href: '/insights/why-canadian-organizations-need-private-ai-infrastructure', kind: 'insight' },
      { anchor: 'AI security', href: '/security', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
  }),
  'pipeda-compliant-ai': buildEditorialLinkMetadata({ cluster: 'complianceGovernance', commercialDestination: '/enterprise' }),
  'healthcare-ai-canada': buildEditorialLinkMetadata({
    cluster: 'healthcareRegulatedIndustries',
    priorityDestinations: ['/healthcare-ai-canada', '/private-ai', '/maplenode', '/ai-model-training', '/data-residency'],
    suggestedInBodyLinks: [
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'product' },
      { anchor: 'MapleNode edge AI appliance', href: '/maplenode', kind: 'product' },
      { anchor: 'AI model training', href: '/ai-model-training', kind: 'commercial' },
      { anchor: 'Why Healthcare AI Requires Provincial and Regional Data Governance', href: '/insights/healthcare-ai-provincial-regional-data-governance', kind: 'insight' },
    ],
    commercialDestination: '/enterprise',
  }),
  'legal-ai-canada': buildEditorialLinkMetadata({ cluster: 'legalConfidentialWorkflows', commercialDestination: '/enterprise' }),
  'canadian-ai-infrastructure': buildEditorialLinkMetadata({
    cluster: 'sovereignAiCanadianInfrastructure',
    priorityDestinations: ['/canadian-ai-infrastructure', '/maplenode', '/mapleos', '/private-ai', '/sovereign-ai-canada'],
    suggestedInBodyLinks: [
      { anchor: 'MapleNode edge AI appliance', href: '/maplenode', kind: 'product' },
      { anchor: 'MapleOS AI Operating System', href: '/mapleos', kind: 'product' },
      { anchor: 'How Canadian AI Infrastructure Can Reduce Foreign Platform Dependency', href: '/insights/canadian-ai-infrastructure-foreign-platform-dependency', kind: 'insight' },
      { anchor: 'Canadian sovereign AI', href: '/sovereign-ai-canada', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
  }),
  'rag-vs-fine-tuning': buildEditorialLinkMetadata({ cluster: 'slmModelTraining', commercialDestination: '/enterprise' }),
  'data-residency': buildEditorialLinkMetadata({ cluster: 'complianceGovernance', commercialDestination: '/enterprise' }),
  security: buildEditorialLinkMetadata({ cluster: 'complianceGovernance', commercialDestination: '/enterprise' }),
  compliance: buildEditorialLinkMetadata({ cluster: 'complianceGovernance', commercialDestination: '/enterprise' }),
  'canadian-ai-platform': buildEditorialLinkMetadata({
    cluster: 'mapleOsAiOperatingSystem',
    priorityDestinations: ['/canadian-ai-platform', '/mapleos', '/private-ai', '/maplenode', '/ai-model-training'],
    suggestedInBodyLinks: [
      { anchor: 'MapleOS AI Operating System', href: '/mapleos', kind: 'product' },
      { anchor: 'MapleNode edge AI appliance', href: '/maplenode', kind: 'product' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'concept' },
      { anchor: 'What Is an AI Operating System?', href: '/insights/what-is-an-ai-operating-system', kind: 'insight' },
    ],
    commercialDestination: '/enterprise',
  }),
  'enterprise-ai': buildEditorialLinkMetadata({ cluster: 'privateAiInfrastructure', commercialDestination: '/enterprise' }),
};

export const seoPageConfigs: Record<SeoPageKey, SeoPageConfig> = Object.fromEntries(
  Object.entries(baseSeoPageConfigs).map(([key, page]) => [
    key,
    {
      ...page,
      editorialLinking: seoPageEditorialMetadata[key as SeoPageKey],
    },
  ]),
) as Record<SeoPageKey, SeoPageConfig>;

export const featuredSeoPages = [
  seoPageConfigs['sovereign-ai-canada'],
  seoPageConfigs['ai-model-training'],
  seoPageConfigs['small-language-model-training'],
  seoPageConfigs['private-ai'],
  seoPageConfigs['pipeda-compliant-ai'],
  seoPageConfigs['canadian-ai-infrastructure'],
];
